import { z } from 'zod';
import { CheckoutWidgetVariantSchema } from '../admin/checkout-widget-ab';

/**
 * POST /api/checkout/create-payment-intent
 * Creates a Stripe PaymentIntent for checkout
 */

/**
 * Shipping address schema for payment intent
 */
export const PaymentIntentShippingAddressSchema = z.object({
  name: z.string(),
  line1: z.string(),
  line2: z.string().optional(),
  city: z.string(),
  state: z.string(),
  postal_code: z.string(),
  country: z.string(),
});

export type PaymentIntentShippingAddress = z.infer<typeof PaymentIntentShippingAddressSchema>;

/**
 * Listing data included in payment intent request
 */
export const PaymentIntentListingSchema = z.object({
  id: z.coerce.number(),
  title: z.string(),
  price: z.number(),
  shippable: z.boolean(),
  user: z.object({
    id: z.coerce.number(),
  }).optional(),
});

export type PaymentIntentListing = z.infer<typeof PaymentIntentListingSchema>;

/**
 * Selected shipping rate for payment intent
 */
export const PaymentIntentShippingRateSchema = z.object({
  id: z.string(),
  shipmentId: z.string().optional(), // Shippo shipment ID for label purchase
  serviceToken: z.string().optional(), // Shippo service level token (e.g., usps_priority) for exact rate matching
  provider: z.string(),
  serviceName: z.string(),
  amount: z.number(),
  estimatedDays: z.number(),
});

export type PaymentIntentShippingRate = z.infer<typeof PaymentIntentShippingRateSchema>;

/**
 * Guest checkout request schema
 */
export const GuestPaymentIntentRequestSchema = z.object({
  mode: z.literal('guest'),
  listingId: z.coerce.number(),
  email: z.string().email(),
  shippingAddress: PaymentIntentShippingAddressSchema,
  listing: PaymentIntentListingSchema,
  selectedShippingRate: PaymentIntentShippingRateSchema.optional(),
  couponId: z.coerce.number().int().positive().optional(),
  couponCode: z.string().optional(),
  /**
   * For CJ Dropshipping listings only: the CJ variant id (vid) the buyer
   * picked on the product page. Server-side validated against the listing's
   * `LISTINGS_METADATA.externalData.variants` — the variant's price overrides
   * `listing.price` so a tampered request body cannot lower the charge.
   */
  variantVid: z.string().optional(),
  /**
   * Partner attribution code (the `?p=` value). The client sends the
   * localStorage-persisted code so attribution survives even when the
   * `partner_code` cookie is missing at checkout. The server prefers this over
   * the cookie and stamps the resolved partner onto the guest user at creation.
   */
  partnerCode: z.string().optional(),
  /**
   * The buyer paid from the Express Checkout buttons (Apple Pay, Google Pay).
   * The wallet handed over the email, name and address, and the intent is
   * confirmed against an Elements group built BEFORE it existed -- so it must
   * carry exactly what that group was built with: card only, and no
   * `setup_future_usage`. A mismatch is refused by Stripe at confirm, after
   * the buyer has already authorised the payment in their wallet.
   */
  express: z.boolean().optional(),
});

export type GuestPaymentIntentRequest = z.infer<typeof GuestPaymentIntentRequestSchema>;

/**
 * Authenticated checkout request schema
 */
export const AuthPaymentIntentRequestSchema = z.object({
  mode: z.literal('authenticated'),
  listingId: z.coerce.number(),
  paymentMethodId: z.string().optional(),
  shippingAddressId: z.number().optional(),
  listing: PaymentIntentListingSchema,
  selectedShippingRate: PaymentIntentShippingRateSchema.optional(),
  couponId: z.coerce.number().int().positive().optional(),
  couponCode: z.string().optional(),
  /**
   * Where to send the receipt, for a buyer whose account carries no email — a
   * phone-only signup, 27.7% of registrations. The account's own address wins
   * whenever there is one; this is only consulted when there is not, and is
   * never written back to the row. Setting an email is an identity change and
   * goes through OTP (/api/user/verify-email), not through checkout.
   */
  email: z.string().email().optional(),
  /**
   * For CJ Dropshipping listings only — see GuestPaymentIntentRequestSchema.
   */
  variantVid: z.string().optional(),
  /**
   * Paid from the Express Checkout buttons (Apple Pay, Google Pay, Link) by a
   * signed-in buyer. The wallet's address is saved to their address book
   * first and named by `shippingAddressId`; the intent is card + Link with no
   * `setup_future_usage`, the same as a guest's express intent, because the
   * buttons' Elements group is built that way before the intent exists.
   */
  express: z.boolean().optional(),
});

export type AuthPaymentIntentRequest = z.infer<typeof AuthPaymentIntentRequestSchema>;

/**
 * Combined payment intent request schema
 */
export const CreatePaymentIntentRequestSchema = z.discriminatedUnion('mode', [
  GuestPaymentIntentRequestSchema,
  AuthPaymentIntentRequestSchema,
]);

export type CreatePaymentIntentRequest = z.infer<typeof CreatePaymentIntentRequestSchema>;

/**
 * Successful payment intent response
 */
export const CreatePaymentIntentSuccessSchema = z.object({
  success: z.literal(true),
  clientSecret: z.string(),
  paymentIntentId: z.string(),
  status: z.string().optional(),
  pendingTransaction: z.boolean().optional(),
  listingId: z.string().optional(),
  message: z.string().optional(),
  /**
   * Guest only: which payment widget to mount -- see
   * schemas/api/admin/checkout-widget-ab.ts. Absent means `styled`, which is
   * also what an open tab from before this field existed will render.
   */
  widgetVariant: CheckoutWidgetVariantSchema.optional(),
  /**
   * What the intent will charge, in cents. Express checkout compares it with
   * the total the wallet sheet showed and refuses to confirm on any
   * difference: the buyer approved one figure and must never be billed
   * another. Absent from responses written before this field existed.
   */
  amount: z.number().int().nonnegative().optional(),
});

export type CreatePaymentIntentSuccess = z.infer<typeof CreatePaymentIntentSuccessSchema>;

/**
 * Payment intent error response
 */
export const CreatePaymentIntentErrorSchema = z.object({
  success: z.literal(false),
  error: z.string(),
  details: z.object({
    formErrors: z.array(z.string()),
    fieldErrors: z.record(z.string(), z.array(z.string())),
  }).optional(),
  message: z.string().optional(),
  listingStatus: z.number().optional(),
  couponFailed: z.boolean().optional(),
  /**
   * The amount we would charge is higher than the price the client displayed —
   * a discount expired, or the seller raised the price, between the product page
   * render and the pay button. The buyer has to see the new figure before
   * anything is billed, so no PaymentIntent was created.
   */
  priceChanged: z.boolean().optional(),
  /** The authoritative item price, in dollars, when `priceChanged` is set. */
  itemPrice: z.number().optional(),
  /**
   * The selected shipping rate could not be re-derived from the carrier that
   * issued it, so the order was refused rather than billed at an unverified
   * amount. The buyer needs to pick a live rate again.
   */
  shippingRateExpired: z.boolean().optional(),
});

export type CreatePaymentIntentError = z.infer<typeof CreatePaymentIntentErrorSchema>;

/**
 * Combined response schema for create-payment-intent
 */
export const CreatePaymentIntentResponseSchema = z.discriminatedUnion('success', [
  CreatePaymentIntentSuccessSchema,
  CreatePaymentIntentErrorSchema,
]);

export type CreatePaymentIntentResponse = z.infer<typeof CreatePaymentIntentResponseSchema>;
